import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const RETRY_MAX = 3;
const RETRY_BASE_MS = 600;
const RETRY_STATUSES = [502, 503, 504];

/* The session credential is the httpOnly `access_token` cookie set by the backend; the browser never sees the JWT. */
const api = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 20000,
});

const readCookie = (name) => {
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : null;
};

api.interceptors.request.use((config) => {
  const csrf = readCookie("csrf_token");
  if (csrf) config.headers["X-CSRF-Token"] = csrf;
  return config;
});

export const isTransientError = (err) =>
  !err?.response || err.code === "ECONNABORTED" || RETRY_STATUSES.includes(err.response?.status);

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

api.interceptors.response.use(
  (res) => {
    if (res.config?._retryToastId) toast.dismiss(res.config._retryToastId);
    return res;
  },
  async (err) => {
    const cfg = err.config;
    if (!cfg || cfg.retry === false || !isTransientError(err)) {
      if (cfg?._retryToastId) toast.dismiss(cfg._retryToastId);
      return Promise.reject(err);
    }
    cfg._retryCount = (cfg._retryCount || 0) + 1;
    if (cfg._retryCount > RETRY_MAX) {
      if (cfg._retryToastId) toast.dismiss(cfg._retryToastId);
      return Promise.reject(err);
    }
    cfg._retryToastId = toast.loading(`Connection issue, retrying… (${cfg._retryCount}/${RETRY_MAX})`, {
      id: cfg._retryToastId || `retry-${cfg.url}`,
    });
    await sleep(RETRY_BASE_MS * 2 ** (cfg._retryCount - 1));
    return api(cfg);
  }
);

export function formatApiError(err) {
  if (err?.code === "ECONNABORTED") return "Request timed out. Please check your connection and try again.";
  if (err && !err.response) return "Cannot reach the server. Check your internet connection and try again.";
  const status = err?.response?.status;
  if (RETRY_STATUSES.includes(status)) return "Server is temporarily unavailable. Please try again in a moment.";
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
